import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, Eye, EyeOff, CheckSquare, Square } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import loginBg from '../../assets/images/Gemini_Generated_Image_5nmpua5nmpua5nmp.png';
import apiClient from '../../api/api';
import { useAdminAuth } from '../../context/AdminAuthContext';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { adminLogin } = useAdminAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.identifier || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setIsLoading(true);

      let deviceId = localStorage.getItem('admin_device_id');
      if (!deviceId) {
        deviceId = crypto.randomUUID();
        localStorage.setItem('admin_device_id', deviceId);
      }

      const response = await apiClient.login({
        identifier: formData.identifier,
        password: formData.password,
        device_id: deviceId,
      });
      
      // Check if user has admin role
      if (response.token && response.user) {
        // Decode JWT to get the role
        try {
          const tokenPayload = JSON.parse(atob(response.token.split('.')[1]));
          let userRole = tokenPayload.role;
          
          // Handle different role formats
          if (typeof userRole === 'object' && userRole !== null) {
            // If role is an object, check if it has SUPER_ADMIN property or value
            userRole = userRole.SUPER_ADMIN ? 'SUPER_ADMIN' : Object.values(userRole)[0];
          } else if (typeof userRole === 'string') {
            // Remove curly braces if present
            userRole = userRole.replace(/[{}]/g, '');
          }

          console.log('Processed user role:', userRole);

          const ADMIN_ROLES = ['SUPER_ADMIN', 'COORDINATOR', 'ACCOUNTS', 'SALES', 'CUSTOM_ROLE', 'RECRUITER'];
          // Staff can have multiple roles (comma-separated from a Postgres array), so check if any of them is an admin role
          const userRoles = String(userRole).split(',').map((r) => r.trim());
          if (userRoles.some((r) => ADMIN_ROLES.includes(r))) {
            adminLogin(response.token, response.user);
            navigate('/admin/dashboard');
          } else {
            setError('Access denied. Admin privileges required.');
          }
        } catch (decodeError) {
          console.error('Error decoding token:', decodeError);
          setError('Invalid authentication token.');
        }
      } else {
        setError('Invalid login response');
      }
    } catch (err) {
      if (err.code === 'DEVICE_REQUIRED' || err.code === 'DEVICE_NOT_AUTHORIZED') {
        setError('This device isn\'t authorized for this account. Contact your SUPER_ADMIN for an activation code.');
      } else {
        setError(err.message || 'Admin login failed. Please try again.');
      }
      console.error('Admin login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-slate-50">
      {/* Left Side - Image & Branding */}
      <div className="hidden lg:flex w-1/2 relative bg-slate-900 overflow-hidden">
        {/* Background Image */}
        <img
          src={loginBg}
          alt="Admin dashboard"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent" />

        {/* Content Overlay */}
        <div className="relative z-10 flex flex-col justify-center h-full p-16 max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-3 mb-6"
          >
            <Shield className="w-12 h-12 text-blue-400" />
            <h1 className="text-5xl font-bold text-white leading-tight">
              Admin Portal
            </h1>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-lg text-slate-300 leading-relaxed font-light"
          >
            Secure access to VCare administrative controls.
            Manage users, monitor operations, and oversee the entire healthcare platform.
          </motion.p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-6 lg:p-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-8 h-8 text-blue-600" />
              <h2 className="text-3xl font-bold text-slate-900">Admin Login</h2>
            </div>
            <p className="text-slate-600 text-sm">
              Enter your administrator credentials to access the control panel.
            </p>
          </motion.div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="space-y-4"
            >
              {/* Username Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 block">
                  Admin Email or Mobile
                </label>
                <div className="relative group">
                  <input
                    type="text"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3.5 pl-4 pr-12 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                    placeholder="Enter admin email or mobile"
                    value={formData.identifier}
                    onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500">
                    <Shield className="w-5 h-5" />
                  </div>
                </div>
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 block">
                  Admin Password
                </label>
                <div className="relative group">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3.5 pl-4 pr-12 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                    placeholder="Enter admin password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Remember Me */}
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => setRememberMe(!rememberMe)}
                  className="flex items-center gap-2 text-sm text-slate-600 hover:text-blue-600 transition-colors"
                >
                  {rememberMe ? (
                    <CheckSquare className="w-4 h-4 text-blue-500 fill-blue-500/10" />
                  ) : (
                    <Square className="w-4 h-4 text-slate-500" />
                  )}
                  Remember admin session
                </button>
              </div>
            </motion.div>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm"
              >
                {error}
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-blue-400 disabled:cursor-not-allowed text-white font-semibold py-3.5 rounded-lg shadow-lg shadow-blue-600/20 transition-all duration-200 transform active:scale-[0.98] flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Authenticating...
                  </>
                ) : (
                  'Access Admin Panel'
                )}
              </button>
            </motion.div>

            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <p className="text-slate-600">
                <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium transition-colors">
                  ← Back to User Login
                </Link>
              </p>
              <p className="text-slate-500 text-sm mt-2">
                <Link to="/admin/activate-device" className="text-blue-400 hover:text-blue-300 font-medium transition-colors">
                  Activate a new device
                </Link>
              </p>
            </motion.div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
